import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Hidden,
  Typography,
  Paper,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { isMobile } from 'react-device-detect';
import loanInstallmentsStyles from './styles';
import type { LoanInstallmentsProps } from '../../../types/account';
import {
  installmentStateLabels,
  intallmentStateDefinitions,
  installmentStateColor,
  createData,
  mapInstallments,
  userInvoiceInjectionStatusLabels,
  userInvoiceInjectionStatusColor,
  injectionStateExplanations,
} from './labels';
import { parseAmountWithCurrency } from '../../../utils/helpers';
import analytics from '../../../utils/analytics';
import InfoDialog from '../../../components/InfoDialog';

const Transition = React.forwardRef((
  props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
  ref: React.Ref<unknown>,
// eslint-disable-next-line react/jsx-props-no-spreading
) => <Slide direction="up" ref={ref} {...props} />);

export default function LoanInstallments(props: LoanInstallmentsProps) {
  const {
    loanInstallments,
    loanUnpaidDelinquencies,
    loanUnpaidCollectionFees,
    loanPaidDelinquencies,
    loanPaidCollectionFees,
    loanCurrency,
  } = props;
  const classes = loanInstallmentsStyles();
  const loanInstallmentsWithUnpaidDelinquencies = mapInstallments(
    loanInstallments,
    [...loanUnpaidDelinquencies, ...loanPaidDelinquencies],
    [...loanUnpaidCollectionFees, ...loanPaidCollectionFees],
  );
  const rows = loanInstallmentsWithUnpaidDelinquencies.map((inst) => createData(
    inst.period,
    inst.expirationDate,
    inst.principal,
    inst.interest,
    inst.amount,
    inst.state,
    loanCurrency,
    inst.userInvoiceInjectionStatus,
    inst.unpaidDelinquency,
    inst.unpaidCollectionFee,
  ));
  const [dialogContent, setDialogContent] = React.useState<typeof rows[0]|null>(null);
  const [open, setOpen] = React.useState(false);
  const handleClose = () => setOpen(false);
  const handleOpen = (row: typeof rows[0]) => {
    setDialogContent(row);
    setOpen(true);
  };

  React.useEffect(() => {
    analytics.page('PU - TU CREDITO', 'CUOTAS PRODUCTO');
  }, []);

  const tableCellHeadStyle = {
    fontWeight: 'bold',
    paddingInline: isMobile ? '11px' : undefined,
  };

  const tableCellBodyStyle = {
    paddingInline: isMobile ? '11px' : undefined,
  };

  return (
    <Fade in {... ({ timeout: 1500 })}>
      <div>
        <Accordion
          expanded
          sx={{
            borderRadius: '15px !important',
            padding: { xs: 1, md: '50px !important' },
          }}
        >
          <AccordionSummary
            expandIcon={<div />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            className={classes.heading}
            sx={{
              '& .MuiAccordionSummary-content': {
                margin: isMobile ? '5px' : undefined,
              },
            }}
          >
            <div style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
            >
              <Typography component="div" color="inherit" fontWeight="bold">
                Detalle cuotas crédito
                <Typography variant="caption" component="div" color="inherit">
                  Fecha:
                  {' '}
                  {new Date().toLocaleDateString()}
                </Typography>

              </Typography>
              <Typography variant="caption" fontSize="0.5rem" color="tertiary.main" className={classes.ResponsiveTitle}>
                Haz click en la cuota
                <br />
                para ver el detalle
              </Typography>
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.AccordionDetails}>
            <TableContainer component={Paper} className={classes.TableContainer}>
              <Table aria-label="simple table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ ...tableCellHeadStyle, paddingRight: isMobile ? '5px' : undefined }}>
                      N°
                      {isMobile ? null : <> cuota</>}
                    </TableCell>
                    <TableCell align="left" style={tableCellHeadStyle}>
                      <div style={{ display: 'flex' }}>
                        <span>
                          Estado
                          {isMobile ? null : <> de la cuota</>}
                        </span>
                        {' '}
                        <InfoDialog
                          dialogContent={(
                            <div>
                              <Typography variant="body2" gutterBottom component="div" sx={{ marginBottom: 3 }}>
                                El estado de la cuota indica si el pago
                                de la cuota se ha realizado correctamente o no.
                                Los estados posibles son:
                              </Typography>
                              {injectionStateExplanations.map((ise) => (
                                <Typography variant="body2" component="div" key={ise.label} style={{ margin: '5px auto' }}>
                                  •
                                  {' '}
                                  <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>{ise.label}</Typography>
                                  {' '}
                                  {ise.explanation}
                                </Typography>
                              ))}
                            </div>
                          )}
                        />
                      </div>
                    </TableCell>
                    <TableCell className={classes.ReponsiveCell} align="left" style={tableCellHeadStyle}>
                      {isMobile
                        ? (
                          <>
                            Fecha
                            <br />
                            pago
                          </>
                        ) : <>Fecha de pago</>}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell className={classes.ReponsiveCell} align="left" style={tableCellHeadStyle}>
                          <div style={{ display: 'flex' }}>
                            <span>
                              Amortización
                              {' '}
                              {(loanCurrency.abbreviation === 'CLF' && <span>(UF)</span>)}
                            </span>
                            <InfoDialog
                              dialogContent={<Typography variant="body2">Monto que se destina para pagar la deuda adquirida con banca.me</Typography>}
                            />
                          </div>
                        </TableCell>
                        <TableCell className={classes.ReponsiveCell} align="left" style={tableCellHeadStyle}>
                          <div style={{ display: 'flex' }}>
                            <span>
                              Interés
                              {' '}
                              {(loanCurrency.abbreviation === 'CLF' && <span>(UF)</span>)}
                            </span>
                            {' '}
                            <InfoDialog
                              dialogContent={<Typography variant="body2">Monto que se destina para pagar el costo del préstamo con banca.me</Typography>}
                            />
                          </div>
                        </TableCell>
                        <TableCell className={classes.ReponsiveCell} align="left" style={tableCellHeadStyle}>
                          <div style={{ display: 'flex' }}>
                            <span>
                              Morosidad
                              {' '}
                              {(loanCurrency.abbreviation === 'CLF' && <span>(UF)</span>)}
                            </span>
                            {' '}
                            <InfoDialog
                              dialogContent={<Typography variant="body2">Intereses generados por el atraso en el pago de la cuota</Typography>}
                            />
                          </div>
                        </TableCell>
                        <TableCell className={classes.ReponsiveCell} align="left" style={tableCellHeadStyle}>
                          <div style={{ display: 'flex' }}>
                            <span>
                              Gastos de cobranza
                              {' '}
                              {(loanCurrency.abbreviation === 'CLF' && <span>(UF)</span>)}
                            </span>
                            <InfoDialog
                              dialogContent={<Typography variant="body2">Cargos adicionales de cobranzas por cuotas pendientes, aplica transcurrido los primeros 20 días de atraso.</Typography>}
                            />
                          </div>
                        </TableCell>
                      </>
                    )}
                    <TableCell align="left" style={tableCellHeadStyle}>
                      {isMobile ? <>Monto</> : <>Monto total cuota</>}
                      {' '}
                      {(loanCurrency.abbreviation === 'CLF' && <span>(UF)</span>)}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow onClick={() => handleOpen(row)} hover key={row.period}>
                      <TableCell style={tableCellBodyStyle}>
                        {row.period}
                      </TableCell>
                      <TableCell align="left" style={{ ...tableCellBodyStyle, color: userInvoiceInjectionStatusColor(row.userInvoiceInjectionStatus, row.state) }}>
                        {row.state === 'PAID' ? 'PAGADA' : userInvoiceInjectionStatusLabels[row.userInvoiceInjectionStatus]}
                      </TableCell>
                      <TableCell className={classes.ReponsiveCell} align="left" style={tableCellBodyStyle}>
                        {new Date(row.expirationDate).toLocaleDateString('es-CL', {
                          day: isMobile ? 'numeric' : '2-digit',
                          month: isMobile ? 'short' : '2-digit',
                          year: isMobile ? undefined : 'numeric',
                        })}
                      </TableCell>
                      {!isMobile && (
                        <>
                          <TableCell className={classes.ReponsiveCell} align="left" style={tableCellBodyStyle}>
                            {parseAmountWithCurrency(row.currency, row.principal, true)}
                          </TableCell>
                          <TableCell className={classes.ReponsiveCell} align="left" style={tableCellBodyStyle}>
                            {parseAmountWithCurrency(row.currency, row.interest, true)}
                          </TableCell>
                          <TableCell className={classes.ReponsiveCell} align="left" style={tableCellBodyStyle}>
                            {parseAmountWithCurrency(row.currency, row.delinquencyAmount, true)}
                          </TableCell>
                          <TableCell align="left" style={tableCellBodyStyle}>
                            {parseAmountWithCurrency(row.currency, row.collectionFeeAmount, true)}
                          </TableCell>
                        </>
                      )}
                      <TableCell align="left" style={tableCellBodyStyle}>
                        {parseAmountWithCurrency(row.currency, row.amount, !isMobile)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
        <Hidden smUp>
          {dialogContent
          && (
            <Dialog
              open={open}
              TransitionComponent={Transition}
              sx={{ textAlign: 'center', '& .MuiDialog-paper': { borderRadius: '12.5px' } }}
            >
              <DialogTitle className={classes.DialogTitle}>
                <Typography variant="h5" fontWeight="bold" component="span" color="tertiary.main">
                  Detalle cuota N°
                  {' '}
                  {dialogContent.period}
                </Typography>
              </DialogTitle>
              <div style={{ backgroundColor: 'whitesmoke', paddingTop: '5px' }}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{
                    display: 'flex', justifyContent: 'center',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Estado</span>
                  <InfoDialog
                    dialogContent={<Typography variant="body2">{intallmentStateDefinitions[dialogContent.state]}</Typography>}
                  />
                </Typography>
                <Typography variant="h5" align="center" style={{ color: installmentStateColor(dialogContent.state), padding: '6px 8px' }}>
                  {installmentStateLabels[dialogContent.state]}

                </Typography>

              </div>
              <DialogContent>
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: '20px', columnGap: '35px',
                }}
                >
                  <div>
                    <Typography variant="body2" align="center">
                      <>
                        Cuota
                        {' '}
                        <span style={{ fontWeight: 600 }}>número</span>
                      </>
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {dialogContent.period}
                    </Typography>

                  </div>
                  <div>
                    <Typography variant="body2" align="center">
                      <span style={{ fontWeight: 600 }}>Vencimiento</span>
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {new Date(dialogContent.expirationDate).toLocaleDateString()}
                    </Typography>

                  </div>
                  <div>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 600 }}>Amortización</span>
                      <InfoDialog
                        dialogContent={<Typography variant="body2">Monto que se destina para pagar la deuda adquirida con banca.me</Typography>}
                      />
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {parseAmountWithCurrency(loanCurrency, dialogContent.principal)}
                    </Typography>

                  </div>
                  <div>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 600 }}>Interés</span>
                      <InfoDialog
                        dialogContent={<Typography variant="body2">Monto que se destina para pagar el costo del préstamo con banca.me</Typography>}
                      />
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {parseAmountWithCurrency(loanCurrency, dialogContent.interest)}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'center' }}>
                      <span style={{ fontWeight: 600 }}>Morosidad</span>
                      <InfoDialog
                        dialogContent={<Typography variant="body2">Intereses generados por el atraso en el pago de la cuota</Typography>}
                      />
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {parseAmountWithCurrency(loanCurrency, dialogContent.delinquencyAmount)}
                    </Typography>
                  </div>
                  <div>
                    <Typography variant="body2" fontWeight="bold" sx={{ display: 'flex', justifyContent: 'center' }}>
                      Gastos de
                      <br />
                      cobranza
                      {' '}
                      <InfoDialog
                        dialogContent={<Typography variant="body2">Cargos adicionales de cobranzas por cuotas pendientes, aplica transcurrido los primeros 20 días de atraso.</Typography>}
                      />
                    </Typography>
                    <Typography variant="h5" align="center" style={{ padding: '6px 8px' }} color="secondary">
                      {parseAmountWithCurrency(loanCurrency, dialogContent.collectionFeeAmount)}
                    </Typography>
                  </div>
                </div>
                <hr />
                <div>
                  <Typography variant="body1" align="center">
                    <>
                      Monto
                      {' '}
                      <span style={{ fontWeight: 600 }}>Cuota</span>
                    </>
                  </Typography>
                  <Typography variant="h1" align="center" style={{ padding: '6px 8px' }} color="primary">
                    {parseAmountWithCurrency(loanCurrency, dialogContent.amount)}
                  </Typography>

                </div>
                <div />
              </DialogContent>
              <DialogActions style={{ backgroundColor: 'whitesmoke' }}>
                <Button onClick={handleClose}>Cerrar</Button>
              </DialogActions>
            </Dialog>
          )}
        </Hidden>
      </div>
    </Fade>
  );
}
